import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ReportGenerationFieldOption,
  ReportGenerationFilterCondition,
  ReportGenerationFilterState,
} from "../report-generation/reportGenerationFiltering";
import {
  addReportGenerationCondition,
  applyReportGenerationTemplate,
  clearReportGenerationFilters,
  createReportGenerationFilterState,
  removeReportGenerationCondition,
  setReportGenerationPrimarySort,
  toggleReportGenerationEntity,
  toggleReportGenerationField,
  toggleReportGenerationPrimarySortDirection,
  updateReportGenerationCondition,
  updateReportGenerationDateRange,
  YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS,
  YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
  YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS,
} from "../report-generation/reportGenerationFiltering";

export type ReportGenerationFilterSliceState = {
  filterState: ReportGenerationFilterState;
  entitySearch: string;
  isReportRun: boolean;
  selectedTemplateId: string | null;
  selectedTemplateSource: "gallery" | "custom" | null;
};

const initialState: ReportGenerationFilterSliceState = {
  filterState: createReportGenerationFilterState(),
  entitySearch: "",
  isReportRun: false,
  selectedTemplateId: null,
  selectedTemplateSource: null,
};

const scholarshipWorksheetFields = YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS.map(
  (column) => column.fieldId,
);

const normalizeWorksheetSelection = (
  filterState: ReportGenerationFilterState,
): ReportGenerationFilterState => {
  if (
    !filterState.selectedEntities.includes(
      YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
    )
  ) {
    return filterState;
  }
  return {
    ...filterState,
    selectedEntities: [YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID],
    selectedFields: [...scholarshipWorksheetFields],
    conditions: [],
    sorts: [
      {
        fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.lastName,
        fieldLabel: "Last Name",
        direction: "asc",
      },
    ],
  };
};

const toggleEntityWithWorksheetExclusivity = (
  filterState: ReportGenerationFilterState,
  entityId: string,
): ReportGenerationFilterState => {
  const worksheetSelected = filterState.selectedEntities.includes(
    YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
  );
  if (
    entityId === YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID &&
    !worksheetSelected
  ) {
    return normalizeWorksheetSelection({
      ...filterState,
      selectedEntities: [YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID],
    });
  }
  const withoutWorksheet =
    entityId !== YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID && worksheetSelected
      ? toggleReportGenerationEntity(
          filterState,
          YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
        )
      : filterState;
  return toggleReportGenerationEntity(withoutWorksheet, entityId);
};

export const reportGenerationFilterSlice = createSlice({
  name: "reportGenerationFilters",
  initialState,
  reducers: {
    setReportGenerationEntitySearch: (state, action: PayloadAction<string>) => {
      state.entitySearch = action.payload;
    },
    toggleReportGenerationEntitySelection: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.filterState = toggleEntityWithWorksheetExclusivity(
        state.filterState,
        action.payload,
      );
      state.isReportRun = false;
      state.selectedTemplateId = null;
      state.selectedTemplateSource = null;
    },
    toggleReportGenerationFieldSelection: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.filterState = state.filterState.selectedEntities.includes(
        YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
      )
        ? normalizeWorksheetSelection(state.filterState)
        : toggleReportGenerationField(state.filterState, action.payload);
      state.isReportRun = false;
      state.selectedTemplateId = null;
      state.selectedTemplateSource = null;
    },
    addReportGenerationFilterCondition: (
      state,
      action: PayloadAction<ReportGenerationFieldOption | null>,
    ) => {
      state.filterState = addReportGenerationCondition(
        state.filterState,
        action.payload,
      );
      state.isReportRun = false;
    },
    updateReportGenerationFilterCondition: (
      state,
      action: PayloadAction<{
        conditionId: string;
        updates: Partial<ReportGenerationFilterCondition>;
      }>,
    ) => {
      state.filterState = updateReportGenerationCondition(
        state.filterState,
        action.payload.conditionId,
        action.payload.updates,
      );
      state.isReportRun = false;
    },
    removeReportGenerationFilterCondition: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.filterState = removeReportGenerationCondition(
        state.filterState,
        action.payload,
      );
      state.isReportRun = false;
    },
    updateReportGenerationDatePreset: (
      state,
      action: PayloadAction<Partial<ReportGenerationFilterState["dateRange"]>>,
    ) => {
      state.filterState = updateReportGenerationDateRange(
        state.filterState,
        action.payload,
      );
      state.isReportRun = false;
    },
    setReportGenerationPrimarySortField: (
      state,
      action: PayloadAction<ReportGenerationFieldOption | null>,
    ) => {
      state.filterState = setReportGenerationPrimarySort(
        state.filterState,
        action.payload,
      );
      state.isReportRun = false;
    },
    toggleReportGenerationPrimarySort: (state) => {
      state.filterState = toggleReportGenerationPrimarySortDirection(
        state.filterState,
      );
      state.isReportRun = false;
    },
    applyReportGenerationTemplateSelection: (
      state,
      action: PayloadAction<{
        templateId: string;
        source: "gallery" | "custom";
        entities: string[];
        fields: string[];
        conditions?: ReportGenerationFilterCondition[];
        sorts?: ReportGenerationFilterState["sorts"];
      }>,
    ) => {
      state.filterState = normalizeWorksheetSelection(
        applyReportGenerationTemplate(action.payload),
      );
      state.selectedTemplateId = action.payload.templateId;
      state.selectedTemplateSource = action.payload.source;
      state.isReportRun = true;
    },
    replaceReportGenerationDefinitionSelection: (
      state,
      action: PayloadAction<{
        filterState: ReportGenerationFilterState;
        templateId?: string | null;
        source?: "gallery" | "custom" | null;
      }>,
    ) => {
      state.filterState = normalizeWorksheetSelection(
        action.payload.filterState,
      );
      state.entitySearch = "";
      state.selectedTemplateId = action.payload.templateId ?? null;
      state.selectedTemplateSource = action.payload.source ?? null;
      state.isReportRun = true;
    },
    markReportGenerationRunRequested: (state) => {
      state.isReportRun = true;
    },
    resetReportGenerationDraft: (state) => {
      state.filterState = createReportGenerationFilterState();
      state.entitySearch = "";
      state.isReportRun = false;
      state.selectedTemplateId = null;
      state.selectedTemplateSource = null;
    },
    clearReportGenerationFilterSelections: (state) => {
      state.filterState = clearReportGenerationFilters();
      state.isReportRun = false;
      state.selectedTemplateId = null;
      state.selectedTemplateSource = null;
    },
  },
});

export const {
  setReportGenerationEntitySearch,
  toggleReportGenerationEntitySelection,
  toggleReportGenerationFieldSelection,
  addReportGenerationFilterCondition,
  updateReportGenerationFilterCondition,
  removeReportGenerationFilterCondition,
  updateReportGenerationDatePreset,
  setReportGenerationPrimarySortField,
  toggleReportGenerationPrimarySort,
  applyReportGenerationTemplateSelection,
  replaceReportGenerationDefinitionSelection,
  markReportGenerationRunRequested,
  resetReportGenerationDraft,
  clearReportGenerationFilterSelections,
} = reportGenerationFilterSlice.actions;
